import React from 'react';
import { usePageContent } from '../../services/contentService';
import LoadingOverlay from '../../components/LoadingOverlay';
import './WhoAreWe.css';
import ImpactStories from '../../components/ImpactStories';
import SEO from '../../components/SEO';

const WhoAreWe = () => {
  const { content, loading, error } = usePageContent('who-are-we');

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0 1rem' : '0 2rem'
  };

  if (loading || !content) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  // Get sections from content
  const heroSection = content?.sections?.find(s => s.type === 'page-hero')?.content;
  const missionVisionSection = content?.sections?.find(s => s.type === 'mission-vision')?.content;

  return (
    <div className="who-are-we-page">
      <SEO
        title="Who Are We"
        description="Learn about YOCILAB, a youth-led innovation hub empowering young Africans to thrive in the modern economy through training, mentorship, and funding."
      />
      <div className="who-are-we-section">
        <div style={containerStyle}>
          <div className="about-section-header">
            <span className="about-section-label">WHO ARE WE</span>
          </div>
          <div className="who-content">
            <div className="image-section">
              <div className="image-overlay-content">
                <h1 className="about-hero-title">
                  {heroSection?.mainTitle || "Ideas.\nSkills. Impact."}
                </h1>
                <button className="cta-button">View all our programs</button>
              </div>
              <img
                src={require('../../assets/YOCILAB/IMG-20220909-WA0024.jpg')}
                alt="YOCILAB team collaboration"
                className="hero-image"
              />
            </div>
            <div className="content-section">
              <p className="main-description">
                {heroSection?.subtitle || "We equip young Africans with the skills, networks, and funding to design solutions that create jobs, strengthen communities, and scale impact."}
              </p>
              <p className="detailed-description">
                {heroSection?.description || "YOCILAB is a youth-led innovation hub empowering young Africans in low-resource communities to thrive in the modern economy. Through training, mentorship, funding, and a supportive ecosystem, we help young people design, launch, and scale innovative solutions in entrepreneurship, civic engagement, climate action, and social impact."}
              </p>
            </div>
          </div>
        </div>
      </div>

      {missionVisionSection && (
        <div className="mission-vision-section">
          <div style={containerStyle}>
            <div className="mission-vision-content">
              <div className="mission-vision-left">
                <div className="section-header">
                  <span className="section-label-small">OUR MISSION AND VISION</span>
                  <h2 className="section-title">
                    {missionVisionSection.mission?.title || "To Empower Young Africans"}
                  </h2>
                  <p className="section-description">
                    {missionVisionSection.mission?.description || "To Empower young Africans with skills, networks, and funding to transform ideas into thriving ventures"}
                  </p>
                </div>
              </div>
              <div className="mission-vision-right">
                <div className="mission-vision-cards">
                  <div className="mission-card">
                    <div className="card-image">
                      <img
                        src={require('../../assets/YOCILAB/IMG-20220909-WA0033.jpg')}
                        alt="Our Mission"
                      />
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{missionVisionSection.mission?.title || "Our Mission"}</h3>
                      <p className="card-description">
                        {missionVisionSection.mission?.description || "Equip Africa's youth to design, build, and scale solutions that create jobs, protect the planet, and strengthen communities."}
                      </p>
                      <button className="read-more-btn">Read More</button>
                    </div>
                  </div>
                  <div className="vision-card">
                    <div className="card-image">
                      <img
                        src={require('../../assets/YOCILAB/IMG-20220909-WA0028 (1).jpg')}
                        alt="Our Vision"
                      />
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{missionVisionSection.vision?.title || "Our Vision"}</h3>
                      <p className="card-description">
                        {missionVisionSection.vision?.description || "An Africa where every young person has the power and opportunity to turn ideas into livelihoods and lasting social change."}
                      </p>
                      <button className="read-more-btn">Read More</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {missionVisionSection?.values && missionVisionSection.values.length > 0 && (
        <div className="values-section">
          <div style={containerStyle}>
            <div className="values-grid">
              {missionVisionSection.values.map((value, index) => (
                <div key={index} className="value-item">
                  <div className="value-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="currentColor" />
                    </svg>
                  </div>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-description">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="our-story-section">
        <div style={containerStyle}>
          <div className="story-content">
            <div className="story-image">
              <img
                src={require('../../assets/YOCILAB/DSC_7431.jpg')}
                alt="YOCILAB Founder"
              />
            </div>
            <div className="story-text">
              <h2 className="story-title">OUR STORY</h2>
              <p className="story-description">
                YOCILAB was born out of the belief that young people in low-resource
                communities have the creativity and drive to solve Africa's most pressing
                challenges; if given the tools, skills, and opportunities. Our founder
                Birindze Bete Dzekewong believe that talented young people shouldn't
                have to leave their community to build a future. Since our founding,
                we've been equipping youth with entrepreneurial skills, connecting them
                to funding, and creating spaces for innovation to thrive.
              </p>
            </div>
          </div>
        </div>
      </div>

      <ImpactStories />
    </div>
  );
};

export default WhoAreWe;